import { useMemo, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  useDisclosure,
  useColorMode,
} from '@chakra-ui/react';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { RejectionReasonStat } from '@texas/api/endpoints/statisticsApi';
import { useTranslation } from 'react-i18next';
import { PaOverviewModal } from '../shared/components/productApprovals/PaOverviewModal';
import { PaTableColumns } from '@texas/components/dashboard/PaTableColumns';
import { FilterOptionType } from '@texas/components/filter/types/types';
import { defaultEmptyFilters } from '@texas/api/endpoints/search/searchPaTypes';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const REASON_COLORS_LIGHT = [
  'rgba(80, 80, 80, 0.8)',
  'rgba(110, 110, 110, 0.8)',
  'rgba(135, 135, 135, 0.8)',
  'rgba(155, 155, 155, 0.8)',
  'rgba(172, 172, 172, 0.8)',
  'rgba(187, 187, 187, 0.8)',
  'rgba(200, 200, 200, 0.8)',
];

const REASON_COLORS_DARK = [
  'rgba(220, 220, 220, 0.85)',
  'rgba(196, 196, 196, 0.85)',
  'rgba(172, 172, 172, 0.85)',
  'rgba(150, 150, 150, 0.85)',
  'rgba(132, 132, 132, 0.85)',
  'rgba(116, 116, 116, 0.85)',
  'rgba(100, 100, 100, 0.85)',
];

interface RejectionReasonsChartProps {
  data: RejectionReasonStat[];
}

export function RejectionReasonsChart({ data }: RejectionReasonsChartProps) {
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const labelColor = isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)';
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [selectedData, setSelectedData] = useState<{
    productApprovalIds: number[];
    reasonName: string;
  } | null>(null);

  const colors = isDark ? REASON_COLORS_DARK : REASON_COLORS_LIGHT;

  const chartData = useMemo(
    () => ({
      labels: data.map((r) =>
        t(`productApproval.rejectReason.${r.reasonName}` as any, {
          defaultValue: r.reasonName,
        }),
      ),
      datasets: [
        {
          data: data.map((r) => r.count),
          backgroundColor: data.map((_, i) => colors[i % colors.length]),
          borderWidth: 0,
        },
      ],
    }),
    [data, t, colors],
  );

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      onClick: (_event, elements) => {
        if (elements.length > 0) {
          const stat = data[elements[0].index];
          if (stat.productApprovalIds.length > 0) {
            setSelectedData({
              productApprovalIds: stat.productApprovalIds,
              reasonName: stat.reasonName,
            });
            onOpen();
          }
        }
      },
      onHover: (event: any, activeElements: any[]) => {
        if (event.native?.target) {
          event.native.target.style.cursor =
            activeElements.length > 0 ? 'pointer' : 'default';
        }
      },
      scales: {
        x: {
          ticks: { color: labelColor },
          grid: { color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
        },
        y: {
          ticks: { color: labelColor },
          grid: { display: false },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            footer: () => String(t('widgets.paLeadTime.clickToViewDetails')),
            label: (context) => {
              const reasonName = context.label;
              const stat = data[context.dataIndex];
              return ` ${reasonName}: ${context.parsed.x} (${stat.percentage}%)`;
            },
          },
        },
      },
    }),
    [t, data, onOpen, labelColor, isDark],
  );

  if (data.length === 0) {
    return (
      <VStack justifyContent="center" height="100%" flex={1}>
        <Text variant="sub">{t('widgets.paRejectionReasons.noData')}</Text>
      </VStack>
    );
  }

  const barHeight = 36;
  const chartHeight = Math.max(200, data.length * barHeight);

  return (
    <>
      <Box height={`${chartHeight}px`} width="100%">
        <Bar data={chartData} options={options} />
      </Box>

      {selectedData && (
        <PaOverviewModal
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setSelectedData(null);
          }}
          filters={{
            ...defaultEmptyFilters,
            productApprovalIds: selectedData.productApprovalIds,
          }}
          searchProps={{
            filters: [FilterOptionType.Branches],
            searchParams: {
              searchTerm: '',
              sortBy: '',
              sortDesc: false,
              page: 1,
              ...defaultEmptyFilters,
              productApprovalIds: selectedData.productApprovalIds,
            },
          }}
          columns={PaTableColumns}
        />
      )}
    </>
  );
}
